import { DestroyRef, Injectable } from '@angular/core';
import { FarmService } from '@shared/services/farm.service';
import { ClientService } from '@shared/services/client.service';
import { CargoPlaceService } from '@shared/services/cargo-place.service';
import {
  shareReplay,
  combineLatest,
  tap,
  map,
  Subject,
  switchMap,
  Observable,
  startWith,
} from 'rxjs';
import { BuyerService } from '@shared/services/buyer.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlowerTypeService } from '@shared/services/flower-type.service';
import { SellerService } from '@shared/services/seller.service';
import { PackageTypeService } from '@shared/services/package-type.service';
import { RateService } from '@shared/services/rate.service';
import { asCatalogDetails, prepareCatalog } from '@shared/utils/catalogs';
import { TrailerTypeService } from '@shared/services/trailer-type.service';
import { CarrierService } from '@shared/services/carrier.service';
import { CatalogDetails, PreparedCatalog } from '../../types/catalog';
import { FlowerType } from '../../types/flower-type';
import { PackageType } from '../../types/package-type';
import { CargoPlace } from '../../types/cargo-place';
import { Farm } from '../../types/farm';
import { Client } from '../../types/client';
import { Rate } from '../../types/rate';
import { Seller } from '../../types/seller';
import { Carrier } from '../../types/carrier';
import { Buyer } from '../../types/buyer';

@Injectable({
  providedIn: 'root',
})
export class CargoCatalogsService {
  fetchFarms$ = new Subject<void>();
  farms$: Observable<PreparedCatalog<Farm>> = this.fetchFarms$.pipe(
    switchMap(() => this.farmService.getAll()),
    map(value => prepareCatalog(this.asCatalogDetails(value), true)),
    tap(value => {
      this.farmMap = this.farmService.toFarmMap(value, 'id');
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  brokerFarms$ = this.farms$.pipe(
    map(farms => farms.filter(f => f.broker)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  fetchClients$ = new Subject<void>();

  clients$: Observable<PreparedCatalog<Client>> = this.fetchClients$.pipe(
    switchMap(() => this.clientService.getAll()),
    map(value => prepareCatalog(this.asCatalogDetails(value), true)),
    tap(value => {
      this.clientMap = this.clientService.toClientMap(value, 'id');
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  cargoPlaces$ = this.cargoPlaceService.getAll().pipe(
    map(value => prepareCatalog(this.asCatalogDetails(value))),
    tap(value => {
      this.cargoPlaceMap = this.cargoPlaceService.toCargoPlaceMap(
        value,
        'code',
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  fetchBuyers$ = new Subject<void>();

  buyers$: Observable<PreparedCatalog<Buyer>> = this.fetchBuyers$.pipe(
    switchMap(() => this.buyerService.getAll()),
    map(value => prepareCatalog(asCatalogDetails(value), true)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  flowerTypes$ = this.flowerTypeService.getAll().pipe(
    map(value => prepareCatalog(this.asCatalogDetails(value))),
    tap(value => {
      this.flowerTypesMap = this.flowerTypeService.toFlowerTypeMap(value, 'id');
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  sellers$ = this.sellerService.getAll().pipe(
    map(value => prepareCatalog(this.asCatalogDetails(value), true)),
    tap(value => {
      this.sellerMap = this.sellerService.toSellerMap(value, 'id');
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  packageTypes$ = this.packageTypeService.getAll().pipe(
    map(value => prepareCatalog(this.asCatalogDetails(value))),
    tap(value => {
      this.packageTypesMap = this.packageTypeService.toPackageTypeMap(
        value,
        'code',
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  rates$ = this.rateService.getAll().pipe(
    map(value => prepareCatalog(this.asCatalogDetails(value))),
    tap(value => {
      this.rateMap = this.rateService.toRateMap(value);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  fetchCarriers$ = new Subject<void>();

  carriers$: Observable<PreparedCatalog<Carrier>> = this.fetchCarriers$.pipe(
    startWith(undefined),
    switchMap(() => this.carrierService.getAll()),
    map(value => prepareCatalog(this.asCatalogDetails(value))),
    tap(value => {
      this.carrierMap = this.carrierService.toCarrierMap(value, 'id');
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  trailersTypes$ = this.trailerTypeService
    .getAll()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  packageTypesMap: Map<string, PackageType> = new Map();
  cargoPlaceMap: Map<string, CargoPlace> = new Map();
  farmMap: Map<string, Farm> = new Map();
  clientMap: Map<string, Client> = new Map();
  rateMap: Map<string, Rate> = new Map();
  sellerMap: Map<string, Seller> = new Map();
  flowerTypesMap: Map<string, FlowerType> = new Map();
  carrierMap: Map<string, Carrier> = new Map();
  constructor(
    private readonly farmService: FarmService,
    private readonly clientService: ClientService,
    private readonly cargoPlaceService: CargoPlaceService,
    private readonly buyerService: BuyerService,
    private readonly flowerTypeService: FlowerTypeService,
    private readonly sellerService: SellerService,
    private readonly packageTypeService: PackageTypeService,
    private readonly rateService: RateService,
    private readonly trailerTypeService: TrailerTypeService,
    private readonly carrierService: CarrierService,
    private readonly destroyRef: DestroyRef,
  ) {}

  private asCatalogDetails<T>(list: T[]): CatalogDetails<T> {
    return {
      updated: '',
      list,
    };
  }

  private getAvailableRequestsForPreloading() {
    return [
      this.farms$,
      this.clients$,
      this.cargoPlaces$,
      this.buyers$,
      this.flowerTypes$,
      this.sellers$,
      this.packageTypes$,
      this.rates$,
      this.trailersTypes$,
    ];
  }

  preload() {
    const requests = this.getAvailableRequestsForPreloading();
    combineLatest(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.fetchFarms$.next();
    this.fetchBuyers$.next();
    this.fetchClients$.next();
  }
}
